import { Component, Inject, OnInit, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { AuthService } from '../../../services/auth.service';
import { LoaderService } from '../../../services/loader.service';
import Swal from 'sweetalert2';
import { DocumentManagementService } from '../../../services/document-management/document-management.service';
import { ResidenceChangeRequestInput, TaxResidenceAddress } from '../../../interfaces/tax/tax-document.interface';
import { RetentionCalculationService } from '../../../services/retention/retention-calculation.service';
import { CommonModule } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { GoogleMapsLoaderService } from '../../../services/google-maps-loader.service';

declare var google: any;

@Component({
  selector: 'app-residence-change-modal',
  templateUrl: './residence-change-modal.component.html',
  styleUrls: ['./residence-change-modal.component.css'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ResidenceChangeModalComponent implements OnInit, AfterViewInit {
  @ViewChild('addressInput') addressInput!: ElementRef;
  
  residenceForm: FormGroup;
  currentAddress: TaxResidenceAddress;
  isAddressDifferent: boolean = false;
  showProofOfAddress: boolean = false;
  selectedFile: File | null = null;
  selectedAddress: string = '';
  autocomplete: any;
  geocoder: any;
  isManualAddress: boolean = false;
  isSpainSelected: boolean = true;
  lastFormReadyState: boolean | null = null;
  showCertificateSection: boolean = true;
  
  // Nuevas propiedades para autosuggest
  supportedCountries = ['ES', 'FR', 'PT', 'IT', 'GB', 'DE']; // Códigos ISO de países soportados
  countryNames = {
    'ES': 'España',
    'FR': 'Francia', 
    'PT': 'Portugal',
    'IT': 'Italia',
    'GB': 'Reino Unido',
    'DE': 'Alemania'
  };
  showPostcodeReminder: boolean = false;
  
  // Lista de países cargada desde Firebase
  availableCountries: Array<{countryName: string, countryCode: string, percentage: number, flagUrl: string}> = [];
  isLoadingCountries: boolean = true;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ResidenceChangeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private authService: AuthService,
    private loader: LoaderService,
    private documentManagementService: DocumentManagementService,
    private cdr: ChangeDetectorRef,
    private retentionCalculationService: RetentionCalculationService,
    private mapsLoader: GoogleMapsLoaderService
  ) {
    const fallbackCountry = data?.currentAddress?.country || 'España';
    this.currentAddress = {
      street: data?.currentAddress?.street || '',
      locality: data?.currentAddress?.locality || '',
      province: data?.currentAddress?.province || '',
      postalCode: data?.currentAddress?.postalCode || '',
      country: fallbackCountry
    };
    
    // Valor por defecto: España
    this.residenceForm = this.fb.group({
      street: ['', Validators.required],
      locality: ['', Validators.required],
      province: ['', Validators.required],
      postalCode: ['', Validators.required],
      country: ['España', Validators.required] // España por defecto
    });
    
    // Inicializar el estado
    this.isSpainSelected = true;
    
    // Inicializar la visibilidad de la sección de certificado (mostrar si distinto país, ocultar si mismo país)
    const defaultCountry = 'España';
    this.showCertificateSection = this.needsCertificate(defaultCountry, this.currentAddress.country);
    
    // Configurar validadores iniciales del código postal (España por defecto)
    setTimeout(() => {
      this.updatePostcodeValidators();
    }, 0);
  }

  ngOnInit() {
    // Cargar Google Maps JS (Places) para habilitar gmp-place-autocomplete
    this.mapsLoader.load(['places']).then(() => {
      console.log('✅ Google Maps cargado correctamente');
      // Google Maps está disponible, el autocomplete funcionará
      this.isManualAddress = false;
      // Forzar detección de cambios después de cargar Google Maps
      setTimeout(() => {
        this.cdr.detectChanges();
        this.testPlaceAutocomplete();
      }, 100);
    }).catch((error) => {
      console.warn('⚠️ Google Maps no está disponible - usando modo manual', error);
      this.isManualAddress = true;
      // Si falla la carga, la UI caerá en modo manual (inputs de texto)
    });
    
    // Cargar lista de países desde Firebase 'retentions'
    this.loadCountriesFromFirebase();
    
    // Verificar la diferencia de dirección con el país preseleccionado
    // Usar setTimeout para asegurar que el formulario esté completamente inicializado
    setTimeout(() => {
      this.checkAddressDifference();
    }, 100);
  }
  
  /**
   * Obtiene la URL de la bandera para un país basado en su código ISO
   * Retorna string vacío para 'Otro' ya que no debe mostrar bandera
   */
  private getCountryFlagUrl(countryName: string, countryCode: string): string {
    // 'Otro' no debe mostrar bandera
    if (countryName === 'Otro') {
      return '';
    }
    // Mapeo de nombres de países en español a códigos ISO
    const countryToIsoMap: { [key: string]: string } = {
      'España': 'es',
      'México': 'mx',
      'Argentina': 'ar',
      'Colombia': 'co',
      'Chile': 'cl',
      'Perú': 'pe',
      'Venezuela': 've',
      'Ecuador': 'ec',
      'Bolivia': 'bo',
      'Uruguay': 'uy',
      'Paraguay': 'py',
      'Brasil': 'br',
      'Estados Unidos': 'us',
      'Estados Unidos de América': 'us',
      'Canadá': 'ca',
      'Reino Unido': 'gb',
      'Francia': 'fr',
      'Alemania': 'de',
      'Italia': 'it',
      'Portugal': 'pt',
      'Países Bajos': 'nl',
      'Bélgica': 'be',
      'Suiza': 'ch',
      'Austria': 'at',
      'Polonia': 'pl',
      'República Checa': 'cz',
      'Hungría': 'hu',
      'Suecia': 'se',
      'Noruega': 'no',
      'Dinamarca': 'dk',
      'Finlandia': 'fi',
      'Irlanda': 'ie',
      'Grecia': 'gr',
      'Chipre': 'cy',
      'Malta': 'mt',
      'Eslovenia': 'si',
      'Eslovaquia': 'sk',
      'Croacia': 'hr',
      'Bulgaria': 'bg',
      'Rumania': 'ro',
      'Lituania': 'lt',
      'Letonia': 'lv',
      'Estonia': 'ee',
      'China': 'cn',
      'Japón': 'jp',
      'Corea': 'kr',
      'India': 'in',
      'Australia': 'au',
      'Nueva Zelanda': 'nz',
      'Sudáfrica': 'za',
      'Rusia': 'ru',
      'Turquía': 'tr',
      'Arabia Saudita': 'sa',
      'Emiratos Árabes Unidos': 'ae',
      'Israel': 'il',
      'Egipto': 'eg',
      'Marruecos': 'ma',
      'Túnez': 'tn',
      'Argelia': 'dz',
      'Nigeria': 'ng',
      'Kenia': 'ke',
      'Ghana': 'gh',
      'Senegal': 'sn',
      'Costa de Marfil': 'ci',
      'Camerún': 'cm',
      'Angola': 'ao',
      'Mozambique': 'mz',
      'Tanzania': 'tz',
      'Etiopía': 'et',
      'Uganda': 'ug',
      'Ruanda': 'rw',
      'Malasia': 'my',
      'Singapur': 'sg',
      'Tailandia': 'th',
      'Vietnam': 'vn',
      'Indonesia': 'id',
      'Filipinas': 'ph',
      'Pakistán': 'pk',
      'Bangladesh': 'bd',
      'Sri Lanka': 'lk',
      'Nepal': 'np',
      'Myanmar': 'mm',
      'Camboya': 'kh',
      'Laos': 'la',
      'Mongolia': 'mn',
      'Kazajistán': 'kz',
      'Uzbekistán': 'uz',
      'Azerbaiyán': 'az',
      'Georgia': 'ge',
      'Armenia': 'am',
      'Bielorrusia': 'by',
      'Ucrania': 'ua',
      'Moldavia': 'md',
      'Albania': 'al',
      'Bosnia y Herzegovina': 'ba',
      'Serbia': 'rs',
      'Montenegro': 'me',
      'Macedonia del Norte': 'mk',
      'Kosovo': 'xk',
      'Islandia': 'is',
      'Luxemburgo': 'lu',
      'Mónaco': 'mc',
      'Liechtenstein': 'li',
      'Andorra': 'ad',
      'San Marino': 'sm',
      'Ciudad del Vaticano': 'va',
      'Cuba': 'cu',
      'República Dominicana': 'do',
      'Puerto Rico': 'pr',
      'Jamaica': 'jm',
      'Trinidad y Tobago': 'tt',
      'Barbados': 'bb',
      'Bahamas': 'bs',
      'Belice': 'bz',
      'Costa Rica': 'cr',
      'El Salvador': 'sv',
      'Guatemala': 'gt',
      'Honduras': 'hn',
      'Nicaragua': 'ni',
      'Panamá': 'pa',
      'Haití': 'ht',
      'Guyana': 'gy',
      'Surinam': 'sr',
      'Guayana Francesa': 'gf',
      'Fiyi': 'fj',
      'Papúa Nueva Guinea': 'pg',
      'Samoa': 'ws',
      'Tonga': 'to',
      'Vanuatu': 'vu',
      'Islas Salomón': 'sb',
      'Nauru': 'nr',
      'Palau': 'pw',
      'Micronesia': 'fm',
      'Marshall': 'mh',
      'Kiribati': 'ki',
      'Tuvalu': 'tv'
    };
    
    // Intentar obtener el código ISO del mapeo
    let isoCode = countryToIsoMap[countryName];
    
    // Si no está en el mapeo, usar el countryCode si está disponible, o generar uno
    if (!isoCode) {
      if (countryCode && countryCode.length >= 2) {
        isoCode = countryCode.toLowerCase();
      } else {
        // Intentar generar código ISO desde las primeras letras del nombre
        const firstTwoLetters = countryName.substring(0, 2).toLowerCase();
        isoCode = firstTwoLetters;
      }
    }
    
    // Generar URL de la bandera
    return `https://cdn.kcak11.com/CountryFlags/countries/${isoCode}.svg`;
  }

  /**
   * Carga la lista de países desde la colección 'retentions' de Firebase
   */
  private loadCountriesFromFirebase(): void {
    this.isLoadingCountries = true;
    // Deshabilitar el control del país mientras se cargan los países
    const countryControl = this.residenceForm.get('country');
    if (countryControl) {
      countryControl.disable();
    }
    
    this.afs.collection('retentions').get().subscribe(
      snapshot => {
        const countries: Array<{countryName: string, countryCode: string, percentage: number, flagUrl: string}> = [];
        
        snapshot.forEach(doc => {
          const data = doc.data() as any;
          
          // Verificar si el documento tiene la estructura esperada
          if (data && typeof data === 'object') {
            // Si el documento contiene un objeto con countryName y percentage
            if (data.countryName && data.percentage !== undefined) {
              // Filtrar 'General' - solo uso interno
              if (data.countryName !== 'General') {
                const countryCode = data.countryCode || '';
                countries.push({
                  countryName: data.countryName,
                  countryCode: countryCode,
                  percentage: data.percentage,
                  flagUrl: this.getCountryFlagUrl(data.countryName, countryCode)
                });
              }
            } else {
              // Si el documento contiene directamente los campos de países
              Object.keys(data).forEach(countryName => {
                // Filtrar 'General' - solo uso interno
                if (countryName !== 'General') {
                  const percentage = data[countryName];
                  if (typeof percentage === 'number') {
                    const countryCode = countryName.substring(0, 2).toUpperCase();
                    countries.push({
                      countryName: countryName,
                      countryCode: countryCode,
                      percentage: percentage,
                      flagUrl: this.getCountryFlagUrl(countryName, countryCode)
                    });
                  }
                }
              });
            }
          }
        });
        
        // Ordenar por nombre de país
        countries.sort((a, b) => a.countryName.localeCompare(b.countryName));
        
        this.availableCountries = countries;
        this.isLoadingCountries = false;
        
        // Habilitar el control del país después de cargar los países
        if (countryControl) {
          countryControl.enable();
        }
        
        // Si el país actual del usuario no está en la lista, agregarlo por defecto
        // (solo si no es 'General' que es solo para uso interno)
        const userCountry = this.currentAddress?.country || 'España';
        const countryExists = this.availableCountries.some(c => c.countryName === userCountry);
        if (!countryExists && userCountry && userCountry !== 'General') {
          const countryCode = userCountry.substring(0, 2).toUpperCase();
          // Obtener el porcentaje correcto de Firebase de forma asíncrona
          this.retentionCalculationService.getCountryRetentionByNameAsync(userCountry).then(percentage => {
            const countryIndex = this.availableCountries.findIndex(c => c.countryName === userCountry);
            if (countryIndex >= 0) {
              this.availableCountries[countryIndex].percentage = percentage;
            }
          });
          this.availableCountries.unshift({
            countryName: userCountry,
            countryCode: countryCode,
            percentage: 19, // Temporal hasta que se cargue desde Firebase
            flagUrl: this.getCountryFlagUrl(userCountry, countryCode)
          });
        }
        
        // Asegurar que España esté seleccionada por defecto si no hay otro país seleccionado
        const currentCountry = this.residenceForm.get('country')?.value;
        if (!currentCountry || currentCountry === '') {
          this.residenceForm.patchValue({ country: 'España' });
          this.isSpainSelected = true;
        } else {
          this.isSpainSelected = (currentCountry === 'España');
        }
        
        // Actualizar la visibilidad de la sección de certificado después de cargar países
        const selectedCountry = this.residenceForm.get('country')?.value || 'España';
        this.showCertificateSection = this.needsCertificate(selectedCountry, this.currentAddress.country);
        
        // Actualizar validadores del código postal según el país inicial
        this.updatePostcodeValidators();
        
        // Verificar la diferencia de dirección después de cargar países
        this.checkAddressDifference();
      },
      error => {
        console.error('Error loading countries from Firebase:', error);
        this.isLoadingCountries = false;
        // En caso de error, usar lista por defecto con España
        this.availableCountries = [{
          countryName: 'España',
          countryCode: 'ES',
          percentage: 19,
          flagUrl: this.getCountryFlagUrl('España', 'ES')
        }];
        
        // Habilitar el control del país incluso si hay error
        const countryControl = this.residenceForm.get('country');
        if (countryControl) {
          countryControl.enable();
        }
      }
    );
  }

  ngAfterViewInit() {
    // Esperar a que Google Maps esté cargado antes de inicializar
    this.mapsLoader.load(['places']).then(() => {
      // Esperar un poco para que el DOM esté completamente renderizado
      setTimeout(() => {
        this.initializeGoogleMaps();
        this.testPlaceAutocomplete();
        
        // Verificar nuevamente la diferencia de dirección después de la inicialización completa
        this.checkAddressDifference();
      }, 100);
    }).catch((error) => {
      console.warn('⚠️ No se pudo cargar Google Maps en ngAfterViewInit', error);
      this.isManualAddress = true;
    });
  }

  private initializeGoogleMaps() {
    try {
      // Verificar que Google Maps esté disponible
      if (!this.mapsLoader.isLoaded()) {
        console.warn('Google Maps no está disponible - usando modo manual');
        this.isManualAddress = true;
        return;
      }

      const google = (window as any).google;
      if (!google || !google.maps || !google.maps.places) {
        console.warn('Google Maps no está disponible - usando modo manual');
        this.isManualAddress = true;
        return;
      }

      // Inicializar el geocoder de Google Maps
      this.geocoder = new google.maps.Geocoder();
      
      // Verificar si el geocoder se inicializó correctamente
      if (!this.geocoder) {
        console.warn('No se pudo inicializar el geocoder - usando modo manual');
        this.isManualAddress = true;
        return;
      }
      
      console.log('✅ Google Maps inicializado correctamente');
    } catch (error) {
      console.error('Error al inicializar Google Maps:', error);
      this.isManualAddress = true;
    }
  }

  private testPlaceAutocomplete() {
    // Verificar si el elemento existe
    const placeAutocomplete = document.querySelector('gmp-place-autocomplete');
    
    if (placeAutocomplete) {
      // Forzar estilos para sobrescribir el tema del sistema
      this.forceAppTheme(placeAutocomplete);
      
      // Probar si podemos agregar un listener manualmente
      try {
        placeAutocomplete.addEventListener('gmp-placeselect', (event: any) => {
          
        });
      } catch (error) {
        console.error('❌ Error al agregar listener manual:', error);
      }
    } else {
      console.warn('❌ No se encontró el elemento gmp-place-autocomplete');
    }
  }

  private forceAppTheme(element: Element) {
    try {
      // Aplicar estilos directamente al elemento
      const htmlElement = element as HTMLElement;
      htmlElement.style.setProperty('color-scheme', 'light', 'important');
      htmlElement.style.setProperty('--color-scheme', 'light', 'important');
      htmlElement.style.setProperty('color', '#495057', 'important');
      htmlElement.style.setProperty('background-color', '#ffffff', 'important');
      
      // Intentar acceder al shadow DOM
      const shadowRoot = (element as any).shadowRoot;
      if (shadowRoot) {
        const input = shadowRoot.querySelector('input');
        if (input) {
          input.style.setProperty('color-scheme', 'light', 'important');
          input.style.setProperty('--color-scheme', 'light', 'important');
          input.style.setProperty('color', '#495057', 'important');
          input.style.setProperty('background-color', '#ffffff', 'important');
          input.style.setProperty('-webkit-text-fill-color', '#495057', 'important');
          input.style.setProperty('text-fill-color', '#495057', 'important');
        }
      }
      
      // Aplicar estilos globales
      this.applyGlobalThemeStyles();
      
    } catch (error) {
      console.error('❌ Error al forzar tema de la aplicación:', error);
    }
  }

  private applyGlobalThemeStyles() {
    // Verificar si ya se aplicaron los estilos
    const existingStyle = document.getElementById('gmp-theme-override');
    if (existingStyle) {
      return;
    }
    
    const style = document.createElement('style');
    style.id = 'gmp-theme-override';
    style.textContent = `
      /* Forzar tema claro en Google Maps */
      gmp-place-autocomplete {
        color-scheme: light !important;
        --color-scheme: light !important;
      }
      
      gmp-place-autocomplete::part(input) {
        color: #495057 !important;
        background-color: #ffffff !important;
        -webkit-text-fill-color: #495057 !important;
        text-fill-color: #495057 !important;
        color-scheme: light !important;
        --color-scheme: light !important;
      }
      
      gmp-place-autocomplete::part(input)::placeholder {
        color: #6c757d !important;
        opacity: 1 !important;
        -webkit-text-fill-color: #6c757d !important;
        text-fill-color: #6c757d !important;
      }
    `;
    
    document.head.appendChild(style);
  }

  private setupManualGeocoding() {
    if (this.addressInput && this.addressInput.nativeElement) {
      const input = this.addressInput.nativeElement;
      
      // Crear un debounce para evitar demasiadas llamadas
      let timeout: any;
      
      input.addEventListener('input', () => {
        clearTimeout(timeout);
        timeout = setTimeout(() => {
          const query = input.value;
          if (query.length > 3) {
            this.searchAddress(query);
          }
        }, 500);
      });
    }
  }

  private async searchAddress(query: string) {
    try {
      if (!this.geocoder) {
        console.warn('Geocoder no disponible - usando modo manual');
        this.isManualAddress = true;
        return;
      }
      
      const request = {
        address: query,
        componentRestrictions: {
          country: 'ES'
        }
      };
      
      this.geocoder.geocode(request, (results: any, status: any) => {
        if (status === 'OK' && results && results.length > 0) {
          
          // Aquí podrías mostrar los resultados en un dropdown personalizado
        } else if (status === 'REQUEST_DENIED') {
          console.error('Solicitud denegada - verificar configuración de API Key');
          this.isManualAddress = true;
        } else if (status === 'OVER_QUERY_LIMIT') {
          console.error('Límite de consultas excedido');
        } else {
          console.warn('Estado de geocoding:', status);
        }
      });
    } catch (error) {
      console.error('Error en búsqueda de dirección:', error);
      this.isManualAddress = true;
    }
  }

  private fillAddressFromPlace(place: any) {
    // console.log removed
    
    let streetNumber = '';
    let route = '';
    let locality = '';
    let administrativeAreaLevel1 = '';
    let administrativeAreaLevel2 = '';
    let postalCode = '';
    let country = '';

    // Extraer información de la dirección
    for (const component of place.address_components) {
      const types = component.types;
      // console.log removed

      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        route = component.long_name;
      }
      if (types.includes('locality')) {
        locality = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        administrativeAreaLevel1 = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        administrativeAreaLevel2 = component.long_name;
      }
      if (types.includes('postal_code')) {
        postalCode = component.long_name;
      }
      if (types.includes('country')) {
        country = component.long_name;
      }
    }

    // Construir la dirección completa (calle primero, luego número)
    const fullAddress = `${route} ${streetNumber}`.trim();
    

    // Preservar el código postal si ya fue introducido manualmente
    const currentPostalCode = this.residenceForm.get('postalCode')?.value;
    let finalPostalCode = currentPostalCode && currentPostalCode.trim() !== '' ? currentPostalCode : postalCode;
    
    // Si el país es España, asegurar que el código postal sea solo numérico
    const detectedCountry = country || 'España';
    if (this.isSpain(detectedCountry) && finalPostalCode) {
      finalPostalCode = finalPostalCode.replace(/\D/g, '').slice(0, 5);
    }
    
    // Limpiar números de provincia y ciudad si el país es España
    let finalLocality = locality;
    let finalProvince = administrativeAreaLevel2 || administrativeAreaLevel1;
    
    if (this.isSpain(detectedCountry)) {
      // Remover números de ciudad y provincia
      finalLocality = locality ? locality.replace(/[0-9]/g, '').trim() : '';
      finalProvince = finalProvince ? finalProvince.replace(/[0-9]/g, '').trim() : '';
    }

    // Actualizar el formulario
    this.residenceForm.patchValue({
      street: fullAddress,
      locality: finalLocality,
      // Prefer the province (level 2) when available; fall back to the broader region.
      province: finalProvince,
      postalCode: finalPostalCode,
      country: detectedCountry
    });

    this.checkAddressDifference();
  }

  private checkAddressDifference() {
    const formValue = this.residenceForm.value;
    const current = this.currentAddress;
    const actualCountry = this.getActualCountry();
    
    this.isAddressDifferent = 
      formValue.street !== current.street ||
      formValue.locality !== current.locality ||
      formValue.province !== current.province ||
      formValue.postalCode !== current.postalCode ||
      actualCountry !== current.country;

    this.showProofOfAddress = this.isAddressDifferent && 
      !!(formValue.street && formValue.locality && formValue.province && 
      formValue.postalCode && actualCountry);
    
    // Actualizar la visibilidad de la sección de certificado
    // Usar el método needsCertificate que incluye la lógica especial para España
    this.showCertificateSection = this.needsCertificate(actualCountry, current.country);
      
    // Verificar si el formulario está listo para enviar
    this.checkFormReady();
  }

  private addressChangeTimeout: any;

  onAddressChange() {
    // Debounce para evitar llamadas excesivas
    if (this.addressChangeTimeout) {
      clearTimeout(this.addressChangeTimeout);
    }
    
    this.addressChangeTimeout = setTimeout(() => {
      this.checkAddressDifference();
      this.checkFormReady();
    }, 300); // 300ms de debounce
  }
  
  // Método para prevenir entrada de letras en código postal cuando es España
  onPostcodeInput(event: any): void {
    const actualCountry = this.getActualCountry();
    const isSpain = this.isSpain(actualCountry);
    
    if (isSpain) {
      // Solo permitir números
      const input = event.target;
      const value = input.value;
      const numericValue = value.replace(/\D/g, '');
      
      // Limitar a 5 dígitos
      const limitedValue = numericValue.slice(0, 5);
      
      if (value !== limitedValue) {
        // Actualizar el valor sin disparar eventos adicionales
        this.residenceForm.patchValue({ postalCode: limitedValue }, { emitEvent: false });
        // Actualizar el input directamente para evitar parpadeo
        input.value = limitedValue;
      }
      
      // Validar después de actualizar
      setTimeout(() => {
        this.validatePostcode();
      }, 0);
    }
  }
  
  // Método para prevenir entrada de números en provincia cuando es España
  onProvinceInput(event: any): void {
    const actualCountry = this.getActualCountry();
    const isSpain = this.isSpain(actualCountry);
    
    if (isSpain) {
      // Solo permitir letras, espacios, guiones y acentos
      const input = event.target;
      const value = input.value;
      // Permitir letras (incluyendo acentos), espacios, guiones y apóstrofes
      const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']/g, '');
      
      if (value !== lettersOnly) {
        // Actualizar el valor sin disparar eventos adicionales
        this.residenceForm.patchValue({ province: lettersOnly }, { emitEvent: false });
        // Actualizar el input directamente para evitar parpadeo
        input.value = lettersOnly;
      }
    }
  }
  
  // Método para prevenir entrada de números en ciudad cuando es España
  onLocalityInput(event: any): void {
    const actualCountry = this.getActualCountry();
    const isSpain = this.isSpain(actualCountry);
    
    if (isSpain) {
      // Solo permitir letras, espacios, guiones y acentos
      const input = event.target;
      const value = input.value;
      // Permitir letras (incluyendo acentos), espacios, guiones y apóstrofes
      const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']/g, '');
      
      if (value !== lettersOnly) {
        // Actualizar el valor sin disparar eventos adicionales
        this.residenceForm.patchValue({ locality: lettersOnly }, { emitEvent: false });
        // Actualizar el input directamente para evitar parpadeo
        input.value = lettersOnly;
      }
    }
  }

  onPlaceChanged(event: any) {
    
    
    if (event.detail && event.detail.place) {
      const place = event.detail.place;
      
      
      // Llenar los campos con los datos de Google Maps
      this.fillAddressFromPlace(place);
      
      // Guardar la dirección formateada completa
      this.selectedAddress = place.formatted_address || '';
      
      
      // Detectar automáticamente el país seleccionado
      this.detectAndUpdateCountry(place);
      
      // Cambiar automáticamente a la vista manual
      this.isManualAddress = true;
      
      
      // Debug: verificar valores del formulario después de llenar
      setTimeout(() => {
        const formValue = this.residenceForm.value;
        
      }, 100);
      
      // Forzar la detección de cambios
      this.cdr.markForCheck();
    }
  }

  private detectAndUpdateCountry(place: any) {
    let detectedCountry = '';
    let countryCode = '';
    
    for (const component of place.address_components) {
      if (component.types.includes('country')) {
        detectedCountry = component.long_name;
        countryCode = component.short_name;
        break;
      }
    }
    
    if (detectedCountry) {
      
      
      // Mapear códigos ISO a nombres en español
      const countryMapping: { [key: string]: string } = {
        'ES': 'España',
        'PT': 'Portugal', 
        'FR': 'Francia',
        'DE': 'Alemania',
        'IT': 'Italia',
        'GB': 'Reino Unido',
        'NL': 'Países Bajos',
        'BE': 'Bélgica',
        'CH': 'Suiza',
        'AT': 'Austria',
        'PL': 'Polonia',
        'CZ': 'República Checa',
        'HU': 'Hungría',
        'SE': 'Suecia',
        'NO': 'Noruega',
        'DK': 'Dinamarca',
        'FI': 'Finlandia',
        'US': 'Estados Unidos',
        'CA': 'Canadá',
        'MX': 'México',
        'BR': 'Brasil',
        'AR': 'Argentina',
        'CL': 'Chile',
        'CO': 'Colombia',
        'PE': 'Perú',
        'UY': 'Uruguay',
        'IE': 'Irlanda',
        'LU': 'Luxemburgo',
        'GR': 'Grecia',
        'CY': 'Chipre',
        'MT': 'Malta',
        'SI': 'Eslovenia',
        'SK': 'Eslovaquia',
        'HR': 'Croacia',
        'BG': 'Bulgaria',
        'RO': 'Rumania',
        'LT': 'Lituania',
        'LV': 'Letonia',
        'EE': 'Estonia'
      };
      
      // Intentar mapear por código ISO primero, luego por nombre
      let mappedCountry = countryMapping[countryCode] || countryMapping[detectedCountry];
      
      if (mappedCountry) {
        // Verificar si el país mapeado está en la lista disponible, si no, agregarlo
        const countryExists = this.availableCountries.some(c => c.countryName === mappedCountry);
        if (!countryExists && mappedCountry) {
          const finalCountryCode = countryCode || mappedCountry.substring(0, 2).toUpperCase();
          // Obtener el porcentaje correcto de Firebase de forma asíncrona
          this.retentionCalculationService.getCountryRetentionByNameAsync(mappedCountry).then(percentage => {
            const countryIndex = this.availableCountries.findIndex(c => c.countryName === mappedCountry);
            if (countryIndex >= 0) {
              this.availableCountries[countryIndex].percentage = percentage;
            }
          });
          this.availableCountries.push({
            countryName: mappedCountry,
            countryCode: finalCountryCode,
            percentage: 19, // Temporal hasta que se cargue desde Firebase
            flagUrl: this.getCountryFlagUrl(mappedCountry, finalCountryCode)
          });
          // Ordenar la lista nuevamente
          this.availableCountries.sort((a, b) => a.countryName.localeCompare(b.countryName));
        }
        
        this.residenceForm.patchValue({
          country: mappedCountry
        });
        
        // Actualizar el estado de España seleccionada
        this.isSpainSelected = (mappedCountry === 'España');
      } else {
        // Si no está en la lista, usar el nombre original y agregarlo a la lista
        const countryExists = this.availableCountries.some(c => c.countryName === detectedCountry);
        if (!countryExists && detectedCountry) {
          const finalCountryCode = countryCode || detectedCountry.substring(0, 2).toUpperCase();
          // Obtener el porcentaje correcto de Firebase de forma asíncrona
          this.retentionCalculationService.getCountryRetentionByNameAsync(detectedCountry).then(percentage => {
            const countryIndex = this.availableCountries.findIndex(c => c.countryName === detectedCountry);
            if (countryIndex >= 0) {
              this.availableCountries[countryIndex].percentage = percentage;
            }
          });
          this.availableCountries.push({
            countryName: detectedCountry,
            countryCode: finalCountryCode,
            percentage: 19, // Temporal hasta que se cargue desde Firebase
            flagUrl: this.getCountryFlagUrl(detectedCountry, finalCountryCode)
          });
          // Ordenar la lista nuevamente
          this.availableCountries.sort((a, b) => a.countryName.localeCompare(b.countryName));
        }
        
        this.residenceForm.patchValue({
          country: detectedCountry
        });
      }
    }
  }



  private disableAutocomplete() {
    try {
      if (this.autocomplete && this.addressInput && this.addressInput.nativeElement) {
        google.maps.event.clearInstanceListeners(this.addressInput.nativeElement);
        this.autocomplete = null;
        // console.log removed
      }
    } catch (error) {
      console.error('Error al deshabilitar autocompletado:', error);
    }
  }

  onUploadAreaClick() {
    const fileInput = document.getElementById('proofFile') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de archivo - incluir múltiples variaciones de tipos MIME
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/x-png', // Variación común de PNG
        'application/x-pdf' // Variación común de PDF
      ];
      
      // También validar por extensión como fallback
      const fileName = file.name.toLowerCase();
      const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
      const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
      
      // Validar tanto por tipo MIME como por extensión
      if (!allowedTypes.includes(file.type) && !hasValidExtension) {
        Swal.fire('Error', 'Solo se permiten archivos PDF, JPG y PNG', 'error');
        return;
      }

      // Validar tamaño (5MB máximo)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire('Error', 'El archivo es demasiado grande. Máximo 5MB', 'error');
        return;
      }

      this.selectedFile = file;
      // console.log removed
      
      // Verificar si el formulario está listo para enviar
      this.checkFormReady();
    }
  }

  isFormReady(): boolean {
    const formValue = this.residenceForm.value;
    const actualCountry = this.getActualCountry();
    
    // Validar país - debe tener un país válido
    const hasCountry = !!actualCountry;
    
    if (!hasCountry) {
      return false;
    }
    
    const isSameCountry = this.isSameCountry(actualCountry, this.currentAddress.country);
    
    // Solo validar dirección si el país es diferente al del perfil
    const hasStreet = !!(formValue.street && formValue.street.trim() !== '');
    const hasCity = !!(formValue.locality && formValue.locality.trim() !== '');
    const hasProvince = !!(formValue.province && formValue.province.trim() !== '');
    const hasPostalCode = !!(formValue.postalCode && formValue.postalCode.trim() !== '');
    const hasAddress = hasStreet && hasCity && hasProvince && hasPostalCode;
    
    // Validar si necesita certificado (mismo país = no; distinto país, incl. a España = sí)
    const needsCert = this.needsCertificate(actualCountry, this.currentAddress.country);
    const hasCertificate = !!this.selectedFile;
    
    // Si es el mismo país, solo necesita el país seleccionado
    // Si es diferente país, necesita dirección y certificado
    const isReady = hasCountry && (isSameCountry || (hasAddress && (!needsCert || hasCertificate)));
    
    return isReady;
  }

  private checkFormReady() {
    // Solo verificar el estado, no hacer nada más
    const isReady = this.isFormReady();
    // console.log removed
  }

  async onSubmit() {
    const formValue = this.residenceForm.value;
    const actualCountry = this.getActualCountry();
    
    // Validar que se haya seleccionado un país
    if (!actualCountry || actualCountry.trim() === '') {
      Swal.fire('Error', 'Por favor selecciona un país de residencia', 'error');
      return;
    }
    
    // console.log removed
    // console.log removed
    // console.log removed
    // console.log removed
    // console.log removed
    // console.log removed
    
    // Verificar si faltan campos obligatorios
    // Solo requerir campos de dirección si el país es diferente al del perfil
    const isSameCountry = this.isSameCountry(actualCountry, this.currentAddress.country);
    const needsStreet = !isSameCountry && (!formValue.street || formValue.street.trim() === '');
    const needsCity = !isSameCountry && (!formValue.locality || formValue.locality.trim() === '');
    const needsProvince = !isSameCountry && (!formValue.province || formValue.province.trim() === '');
    const needsPostcode = !isSameCountry && (!formValue.postalCode || formValue.postalCode.trim() === '');

    if (needsStreet || needsCity || needsProvince || needsPostcode) {
      // console.log removed
      await this.showPostcodePopup();
      return; // No continuar hasta que se completen los campos
    }

    if (!this.isFormReady()) {
      // Verificar si falta certificado (requerido al cambiar de país, incl. a España desde el extranjero)
      const needsCert = this.needsCertificate(actualCountry, this.currentAddress.country);
      const hasCertificate = !!this.selectedFile;
      
      if (needsCert && !hasCertificate) {
        Swal.fire('Error', 'Debes subir un certificado de residencia fiscal para cambiar a un país diferente al actual', 'error');
      } else {
        Swal.fire('Error', 'Por favor completa todos los campos requeridos', 'error');
      }
      return;
    }
    
    // Verificar si es el mismo país para auto-aprobar
    // Solo se auto-aprueba si el cambio es dentro del mismo país (no si cambia a España desde otro país)
    const shouldAutoApprove = isSameCountry;

    try {
      console.log('🔍 [DEBUG] ResidenceChangeModal - Iniciando envío de solicitud');
      console.log('🔍 [DEBUG] ResidenceChangeModal - País solicitado:', actualCountry, 'Mismo país:', isSameCountry);
      console.log('🔍 [DEBUG] ResidenceChangeModal - Auto-aprobar:', shouldAutoApprove);
      this.loader.showSpinner(shouldAutoApprove ? 'Procesando solicitud...' : 'Enviando solicitud de cambio de residencia...');
      let documentUrl = '';

      // Si hay archivo, subirlo primero
      if (this.selectedFile) {
        console.log('🔍 [DEBUG] ResidenceChangeModal - Subiendo archivo:', this.selectedFile.name);
        const userAuth = this.authService.userAuth.getValue();
        if (!userAuth) {
          throw new Error('No hay usuario autenticado');
        }

        const filePath = `residence-proofs/${userAuth.uid}/${Date.now()}_${this.selectedFile.name}`;
        console.log('🔍 [DEBUG] ResidenceChangeModal - Ruta del archivo:', filePath);
        const fileRef = this.storage.ref(filePath);
        const task = this.storage.upload(filePath, this.selectedFile);
        
        await task;
        documentUrl = await fileRef.getDownloadURL().toPromise();
        console.log('🔍 [DEBUG] ResidenceChangeModal - Archivo subido exitosamente, URL:', documentUrl);
      } else {
        console.log('🔍 [DEBUG] ResidenceChangeModal - No hay archivo para subir');
      }

      // Calcular porcentajes de retención para ambos países
      console.log('🔍 [DEBUG] ResidenceChangeModal - Calculando porcentajes de retención');
      // Usar el método auxiliar que busca primero en availableCountries y luego en Firebase
      const currentCountryRetention = await this.getCountryRetentionPercentage(this.currentAddress.country);
      const requestedCountryRetention = await this.getCountryRetentionPercentage(actualCountry);
      
      console.log('🔍 [DEBUG] ResidenceChangeModal - País actual:', this.currentAddress.country, 'Retención:', currentCountryRetention);
      console.log('🔍 [DEBUG] ResidenceChangeModal - País solicitado:', actualCountry, 'Retención:', requestedCountryRetention);

      // Crear la solicitud de cambio de residencia
      console.log('🔍 [DEBUG] ResidenceChangeModal - Creando objeto de solicitud');
      const request: ResidenceChangeRequestInput = {
        currentCountry: this.currentAddress.country,
        requestedCountry: actualCountry, // Usar el país seleccionado
        currentCountryRetentionPercentage: currentCountryRetention,
        requestedCountryRetentionPercentage: requestedCountryRetention,
        currentAddress: this.currentAddress,
        requestedAddress: {
          street: formValue.street,
          locality: formValue.locality,
          province: formValue.province,
          postalCode: formValue.postalCode,
          country: actualCountry // Usar el país real
        },
        documentUrl: documentUrl,
        isSameCountry: shouldAutoApprove  // Auto-aprobar solo si el cambio es dentro del mismo país
      };

      console.log('🔍 [DEBUG] ResidenceChangeModal - Objeto de solicitud creado:', request);

      // Enviar la solicitud usando el servicio
      console.log('🔍 [DEBUG] ResidenceChangeModal - Enviando solicitud al servicio');
      await this.documentManagementService.requestResidenceChange(request);
      console.log('🔍 [DEBUG] ResidenceChangeModal - Solicitud enviada exitosamente');

      this.loader.hideSpinner();
      
      if (shouldAutoApprove) {
        await Swal.fire({
          icon: 'success',
          title: 'Solicitud aprobada',
          text: 'Tu solicitud ha sido aprobada automáticamente al cambiar de dirección dentro del mismo país de residencia.',
          confirmButtonText: 'Aceptar'
        });
      } else {
        await Swal.fire({
          icon: 'success',
          title: 'Solicitud enviada',
          text: 'Tu solicitud de cambio de residencia ha sido enviada con éxito. Será revisada por nuestro equipo.',
          confirmButtonText: 'Aceptar'
        });
      }

      this.dialogRef.close({ success: true, selectedCountry: actualCountry });
    } catch (error: any) {
      this.loader.hideSpinner();
      console.error('🔍 [DEBUG] ResidenceChangeModal - Error al enviar solicitud:', error);
      console.error('🔍 [DEBUG] ResidenceChangeModal - Tipo de error:', typeof error);
      console.error('🔍 [DEBUG] ResidenceChangeModal - Mensaje de error:', error.message);
      console.error('🔍 [DEBUG] ResidenceChangeModal - Stack trace:', error.stack);
      
      let errorMessage = 'Ha ocurrido un error al enviar tu solicitud. Por favor intenta de nuevo.';
      
      if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
        confirmButtonText: 'Aceptar'
      });
    }
  }

  // Método para alternar entre autosuggest y dirección manual
  toggleManualAddress(): void {
    this.isManualAddress = !this.isManualAddress;
    
    if (this.isManualAddress) {
      // Limpiar el autocomplete si existe
      if (this.autocomplete) {
        this.autocomplete = null;
      }
    }
    // No necesitamos reinicializar Google Maps con la nueva API
  }

  // Método para verificar si el país seleccionado soporta autosuggest
  isCountrySupportedForAutosuggest(): boolean {
    const actualCountry = this.getActualCountry();
    const countryCode = this.getCountryCode(actualCountry);
    const isSupported = this.supportedCountries.includes(countryCode);
    
    
    return isSupported;
  }

  // Método para obtener el código ISO del país
  getCountryCode(countryName: string): string {
    const countryMap: { [key: string]: string } = {
      'España': 'ES',
      'Francia': 'FR',
      'Portugal': 'PT',
      'Italia': 'IT',
      'Reino Unido': 'GB',
      'Alemania': 'DE'
    };
    const countryCode = countryMap[countryName] || '';
    // console.log removed
    return countryCode;
  }

  // Método para validar el postcode
  validatePostcode(): void {
    const postcode = this.residenceForm.get('postalCode')?.value;
    const actualCountry = this.getActualCountry();
    const isSpain = this.isSpain(actualCountry);
    
    if (isSpain) {
      // Para España: solo números, máximo 5 dígitos
      const numericPostcode = postcode ? postcode.replace(/\D/g, '') : '';
      if (numericPostcode !== postcode) {
        // Si hay caracteres no numéricos, actualizar el campo
        this.residenceForm.patchValue({ postalCode: numericPostcode }, { emitEvent: false });
      }
      // Validar que tenga 5 dígitos
      this.showPostcodeReminder = !numericPostcode || numericPostcode.length !== 5;
    } else {
      // Para otros países: solo verificar que no esté vacío
      this.showPostcodeReminder = !postcode || postcode.trim() === '';
    }
  }
  
  // Validador personalizado para código postal español
  private spanishPostcodeValidator(control: any): { [key: string]: any } | null {
    const value = control.value;
    if (!value) {
      return null; // Dejar que Validators.required maneje el caso vacío
    }
    
    // Solo números y exactamente 5 dígitos
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length !== 5) {
      return { invalidSpanishPostcode: true };
    }
    
    return null;
  }
  
  // Método para actualizar validadores del código postal según el país
  private updatePostcodeValidators(): void {
    const actualCountry = this.getActualCountry();
    const isSpain = this.isSpain(actualCountry);
    const postalCodeControl = this.residenceForm.get('postalCode');
    
    if (postalCodeControl) {
      if (isSpain) {
        // Para España: requerido + validador personalizado
        postalCodeControl.setValidators([Validators.required, this.spanishPostcodeValidator.bind(this)]);
      } else {
        // Para otros países: solo requerido
        postalCodeControl.setValidators([Validators.required]);
      }
      postalCodeControl.updateValueAndValidity();
    }
  }


  // Método para mostrar popup de código postal
  async showPostcodePopup(): Promise<void> {
    const formValue = this.residenceForm.value;
    
    // console.log removed
    // console.log removed
    
    // Determinar qué campos faltan
    const needsStreet = !formValue.street || formValue.street.trim() === '';
    const needsCity = !formValue.locality || formValue.locality.trim() === '';
    const needsPostcode = !formValue.postalCode || formValue.postalCode.trim() === '';
    const needsProvince = !formValue.province || formValue.province.trim() === '';
    
    // console.log removed
    
    // Si no faltan campos obligatorios, no mostrar popup
    if (!needsStreet && !needsCity && !needsPostcode) {
      // console.log removed
      return;
    }
    
    // Construir HTML dinámicamente solo para campos faltantes
    let fieldsHtml = '';
    if (needsStreet) {
      fieldsHtml += `
        <div style="margin-bottom: 1.5rem; text-align: center;">
          <label style="display: block; margin-bottom: 0.75rem; font-weight: 600; color: #2c3e50; font-size: 1rem;">Calle *</label>
          <input type="text" id="popup-street" class="swal2-input" placeholder="Ej: Calle Mayor, 123" style="width: 80%; max-width: 300px; border: 2px solid #e9ecef; border-radius: 8px; padding: 0.875rem; font-size: 1rem; text-align: center; transition: border-color 0.2s ease;">
        </div>
      `;
    }
    if (needsPostcode) {
      const actualCountry = this.getActualCountry();
      const isSpain = this.isSpain(actualCountry);
      const placeholder = isSpain ? 'Ej: 28001 (solo números)' : 'Ej: 28001';
      const maxLength = isSpain ? 'maxlength="5"' : '';
      const pattern = isSpain ? 'pattern="[0-9]*"' : '';
      
      fieldsHtml += `
        <div style="margin-bottom: 1.5rem; text-align: center;">
          <label style="display: block; margin-bottom: 0.75rem; font-weight: 600; color: #2c3e50; font-size: 1rem;">Código postal *</label>
          <input type="text" id="popup-postcode" class="swal2-input" placeholder="${placeholder}" ${maxLength} ${pattern} style="width: 80%; max-width: 300px; border: 2px solid #e9ecef; border-radius: 8px; padding: 0.875rem; font-size: 1rem; text-align: center; transition: border-color 0.2s ease;">
          ${isSpain ? '<small style="display: block; margin-top: 0.5rem; color: #6c757d; font-size: 0.875rem;">Solo números (5 dígitos)</small>' : ''}
        </div>
      `;
    }
    if (needsCity) {
      const actualCountry = this.getActualCountry();
      const isSpain = this.isSpain(actualCountry);
      const placeholder = isSpain ? 'Ej: Madrid (solo letras)' : 'Ej: Madrid';
      
      fieldsHtml += `
        <div style="margin-bottom: 1.5rem; text-align: center;">
          <label style="display: block; margin-bottom: 0.75rem; font-weight: 600; color: #2c3e50; font-size: 1rem;">Ciudad</label>
          <input type="text" id="popup-city" class="swal2-input" placeholder="${placeholder}" style="width: 80%; max-width: 300px; border: 2px solid #e9ecef; border-radius: 8px; padding: 0.875rem; font-size: 1rem; text-align: center; transition: border-color 0.2s ease;">
          ${isSpain ? '<small style="display: block; margin-top: 0.5rem; color: #6c757d; font-size: 0.875rem;">Solo letras (sin números)</small>' : ''}
        </div>
      `;
    }
    if (needsProvince) {
      const actualCountry = this.getActualCountry();
      const isSpain = this.isSpain(actualCountry);
      const placeholder = isSpain ? 'Ej: Madrid (solo letras)' : 'Ej: Madrid';
      
      fieldsHtml += `
        <div style="margin-bottom: 1.5rem; text-align: center;">
          <label style="display: block; margin-bottom: 0.75rem; font-weight: 600; color: #2c3e50; font-size: 1rem;">Provincia *</label>
          <input type="text" id="popup-province" class="swal2-input" placeholder="${placeholder}" style="width: 80%; max-width: 300px; border: 2px solid #e9ecef; border-radius: 8px; padding: 0.875rem; font-size: 1rem; text-align: center; transition: border-color 0.2s ease;">
          ${isSpain ? '<small style="display: block; margin-top: 0.5rem; color: #6c757d; font-size: 0.875rem;">Solo letras (sin números)</small>' : ''}
        </div>
      `;
    }

    // Obtener el país real para mostrar en la dirección
    const actualCountry = this.getActualCountry();
    
    // Usar la dirección seleccionada o construir una desde el formulario
    const displayAddress = this.selectedAddress || `${formValue.street || ''}, ${formValue.locality || ''}, ${formValue.province || ''}, ${actualCountry || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '');

    const { value: formValues } = await Swal.fire({
      title: 'Completar información de la dirección',
      html: `
        <div style="margin-bottom: 2rem; padding: 1.5rem; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-radius: 12px; border-left: 5px solid #ff6b35; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 0.75rem;">
            <span style="font-size: 1.4rem; margin-right: 0.75rem; color: #ff6b35;">📍</span>
            <strong style="color: #2c3e50; font-size: 1rem; font-weight: 600;">Dirección seleccionada:</strong>
          </div>
          <div style="color: #495057; font-size: 0.95rem; line-height: 1.5; font-weight: 500; text-align: center;">${displayAddress}</div>
        </div>
        
        <div style="text-align: center;">
          ${fieldsHtml}
        </div>
      `,
      width: '520px',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Completar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ff6b35',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal2-popup-professional',
        title: 'swal2-title-professional',
        confirmButton: 'swal2-confirm-professional',
        cancelButton: 'swal2-cancel-professional'
      },
      didOpen: () => {
        // Agregar listeners para validación cuando es España
        const actualCountry = this.getActualCountry();
        const isSpain = this.isSpain(actualCountry);
        
        setTimeout(() => {
          // Listener para código postal (solo números)
          if (needsPostcode && isSpain) {
            const postcodeInput = document.getElementById('popup-postcode') as HTMLInputElement;
            if (postcodeInput) {
              postcodeInput.addEventListener('input', (e: any) => {
                const value = e.target.value;
                const numericValue = value.replace(/\D/g, '').slice(0, 5);
                if (value !== numericValue) {
                  e.target.value = numericValue;
                }
              });
            }
          }
          
          // Listener para ciudad (solo letras)
          if (needsCity && isSpain) {
            const cityInput = document.getElementById('popup-city') as HTMLInputElement;
            if (cityInput) {
              cityInput.addEventListener('input', (e: any) => {
                const value = e.target.value;
                const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']/g, '');
                if (value !== lettersOnly) {
                  e.target.value = lettersOnly;
                }
              });
            }
          }
          
          // Listener para provincia (solo letras)
          if (needsProvince && isSpain) {
            const provinceInput = document.getElementById('popup-province') as HTMLInputElement;
            if (provinceInput) {
              provinceInput.addEventListener('input', (e: any) => {
                const value = e.target.value;
                const lettersOnly = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']/g, '');
                if (value !== lettersOnly) {
                  e.target.value = lettersOnly;
                }
              });
            }
          }
        }, 100);
      },
      preConfirm: () => {
        // Obtener valores del popup o del formulario según corresponda
        let street = formValue.street;
        let postcode = formValue.postalCode;
        let city = formValue.locality;
        let province = formValue.province;
        
        // Si el campo se está mostrando en el popup, obtener su valor del input
        if (needsStreet) {
          const streetInput = document.getElementById('popup-street') as HTMLInputElement;
          street = streetInput ? streetInput.value : '';
        }
        if (needsPostcode) {
          const postcodeInput = document.getElementById('popup-postcode') as HTMLInputElement;
          postcode = postcodeInput ? postcodeInput.value : '';
          
          // Si el país es España, limpiar el código postal para que solo tenga números
          const actualCountry = this.getActualCountry();
          const isSpain = this.isSpain(actualCountry);
          if (isSpain && postcode) {
            postcode = postcode.replace(/\D/g, '').slice(0, 5);
          }
        }
        if (needsCity) {
          const cityInput = document.getElementById('popup-city') as HTMLInputElement;
          city = cityInput ? cityInput.value : '';
          
          // Si el país es España, limpiar números de la ciudad
          const actualCountry = this.getActualCountry();
          const isSpain = this.isSpain(actualCountry);
          if (isSpain && city) {
            city = city.replace(/[0-9]/g, '').trim();
          }
        }
        if (needsProvince) {
          const provinceInput = document.getElementById('popup-province') as HTMLInputElement;
          province = provinceInput ? provinceInput.value : '';
          
          // Si el país es España, limpiar números de la provincia
          const actualCountry = this.getActualCountry();
          const isSpain = this.isSpain(actualCountry);
          if (isSpain && province) {
            province = province.replace(/[0-9]/g, '').trim();
          }
        }
        
        
        // Solo validar los campos que se están mostrando en el popup
        if (needsStreet && (!street || street.trim() === '')) {
          Swal.showValidationMessage('La calle es obligatoria');
          return false;
        }
        if (needsCity && (!city || city.trim() === '')) {
          Swal.showValidationMessage('La ciudad es obligatoria');
          return false;
        }
        if (needsProvince && (!province || province.trim() === '')) {
          Swal.showValidationMessage('La provincia es obligatoria');
          return false;
        }
        if (needsPostcode) {
          const actualCountry = this.getActualCountry();
          const isSpain = this.isSpain(actualCountry);
          
          if (!postcode || postcode.trim() === '') {
            Swal.showValidationMessage('El código postal es obligatorio');
            return false;
          }
          
          // Validación adicional para España
          if (isSpain) {
            const numericPostcode = postcode.replace(/\D/g, '');
            if (numericPostcode.length !== 5) {
              Swal.showValidationMessage('El código postal español debe tener exactamente 5 dígitos numéricos');
              return false;
            }
          }
        }
        
        return { street, postcode, city, province };
      }
    });

    if (formValues) {
      this.residenceForm.patchValue({ 
        street: formValues.street,
        postalCode: formValues.postcode,
        locality: formValues.city,
        province: formValues.province
      });
      
      this.showPostcodeReminder = false;
      // console.log removed
      
      // Mostrar vista previa de la dirección completa
      await this.showAddressPreview();
    }
  }

  // Método para mostrar vista previa de la dirección completa
  async showAddressPreview(): Promise<void> {
    const formValue = this.residenceForm.value;
    const actualCountry = this.getActualCountry();
    const fullAddress = `${formValue.street || ''}, ${formValue.postalCode || ''} ${formValue.locality || ''}, ${formValue.province || ''}, ${actualCountry || ''}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '');
    
    await Swal.fire({
      title: 'Vista previa de la dirección',
      html: `
        <div style="text-align: left; margin: 20px 0;">
          <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
            <p style="margin: 0; font-weight: 500; color: #333;">📍 Dirección completa:</p>
            <p style="margin: 5px 0 0 0; color: #666; font-size: 0.9rem; line-height: 1.4;">${fullAddress}</p>
          </div>
          <p style="margin-top: 15px; color: #666; font-size: 0.9rem;">¿Es correcta esta información?</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Editar',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d'
    }).then((result) => {
      if (result.isDismissed && result.dismiss === Swal.DismissReason.cancel) {
        // Si el usuario quiere editar, volver a mostrar el popup
        this.showPostcodePopup();
      }
      // Si confirma, continuar con el proceso normal
    });
  }

  // Método para verificar si el formulario básico está listo (sin postcode obligatorio)
  isBasicFormReady(): boolean {
    const actualCountry = this.getActualCountry();
    
    // Validar que haya un país seleccionado
    return !!actualCountry && actualCountry.trim() !== '';
  }

  // Método para obtener las restricciones de componente para la nueva API
  getComponentRestrictions(): any {
    const actualCountry = this.getActualCountry();
    const countryCode = this.getCountryCode(actualCountry);
    return { country: [countryCode] };
  }

  // Método para manejar la selección de lugar con la nueva API
  async onPlaceSelected(event: any): Promise<void> {
    
    
    try {
      const placePrediction = event.placePrediction;
      
      
      if (placePrediction) {
        
        
        // Convertir la predicción a un objeto Place
        const place = placePrediction.toPlace();
        
        
        // Obtener los campos necesarios
        
        await place.fetchFields({ 
          fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'] 
        });
        
        
        
        
        // Guardar la dirección formateada
        this.selectedAddress = place.formattedAddress || '';
        
        
        // Limpiar el formulario antes de llenar con nuevos datos
        this.clearAddressForm();
        
        // Extraer componentes de la dirección usando la nueva API
        this.fillAddressFromNewPlace(place);
        
        // Cambiar automáticamente a la vista manual
        this.isManualAddress = true;
        
        
        // Forzar detección de cambios
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        
        // Debug: verificar valores del formulario después de llenar
        setTimeout(() => {
          const formValue = this.residenceForm.value;
          
        }, 100);
      } else {
        console.warn('⚠️ No se encontró placePrediction en el evento');
        // Fallback a modo manual si no hay predicción
        this.isManualAddress = true;
      }
    } catch (error: any) {
      console.error('❌ Error al procesar lugar seleccionado:', error);
      console.error('❌ Stack trace:', error?.stack);
      
      // Si hay error con la API, cambiar a modo manual
      this.isManualAddress = true;
      
      // Mostrar mensaje al usuario
      if (error.message && error.message.includes('403')) {
        console.error('🚫 Error 403: Verificar configuración de API Key en Google Cloud Console');
      }
    }
  }

  // Método para extraer datos de la nueva API de Places
  private fillAddressFromNewPlace(place: any): void {
    // console.log removed
    // console.log removed
    // console.log removed
    
    let streetNumber = '';
    let route = '';
    let locality = '';
    let administrativeAreaLevel1 = '';
    let administrativeAreaLevel2 = '';
    let postalCode = '';
    let country = '';

    // Extraer información de los componentes de dirección
    if (place.addressComponents && place.addressComponents.length > 0) {
      // console.log removed
      
      for (let i = 0; i < place.addressComponents.length; i++) {
        const component = place.addressComponents[i];
        const types = component.types;
        // console.log removed
        // console.log removed

        if (types.includes('street_number')) {
          streetNumber = component.longText;
          // console.log removed
        }
        if (types.includes('route')) {
          route = component.longText;
          // console.log removed
        }
        if (types.includes('locality')) {
          locality = component.longText;
          // console.log removed
        }
        if (types.includes('administrative_area_level_1')) {
          administrativeAreaLevel1 = component.longText;
        }
        if (types.includes('administrative_area_level_2')) {
          administrativeAreaLevel2 = component.longText;
          // console.log removed
        }
        if (types.includes('postal_code')) {
          postalCode = component.longText;
          // console.log removed
        }
        if (types.includes('country')) {
          country = component.longText;
          // console.log removed
        }
      }
    } else {
      console.warn('⚠️ No se encontraron componentes de dirección');
    }

    // Construir la dirección completa (calle primero, luego número)
    let fullAddress = `${route} ${streetNumber}`.trim();
    
    // Si no se encontró street_number ni route, usar el primer componente como dirección
    if (!fullAddress && place.addressComponents && place.addressComponents.length > 0) {
      // Buscar el primer componente que no sea país, provincia o ciudad
      for (const component of place.addressComponents) {
        const types = component.types;
        if (!types.includes('country') && 
            !types.includes('administrative_area_level_1') && 
            !types.includes('locality') &&
            !types.includes('postal_code')) {
          fullAddress = component.longText;
          // console.log removed
          break;
        }
      }
    }
    

    // Usar el código postal de la nueva dirección seleccionada
    let finalPostalCode = postalCode;
    
    // Mapear el país detectado a los nombres en español
    const mappedCountry = this.mapCountryToSpanish(country);
    
    // Si el país es España, asegurar que el código postal sea solo numérico
    if (this.isSpain(mappedCountry) && finalPostalCode) {
      finalPostalCode = finalPostalCode.replace(/\D/g, '').slice(0, 5);
    }
    
        // Verificar si el país mapeado está en la lista disponible, si no, agregarlo
        const countryExists = this.availableCountries.some(c => c.countryName === mappedCountry);
        if (!countryExists && mappedCountry) {
          const countryCode = mappedCountry.substring(0, 2).toUpperCase();
          // Obtener el porcentaje correcto de Firebase de forma asíncrona
          this.retentionCalculationService.getCountryRetentionByNameAsync(mappedCountry).then(percentage => {
            const countryIndex = this.availableCountries.findIndex(c => c.countryName === mappedCountry);
            if (countryIndex >= 0) {
              this.availableCountries[countryIndex].percentage = percentage;
            }
          });
          this.availableCountries.push({
            countryName: mappedCountry,
            countryCode: countryCode,
            percentage: 19, // Temporal hasta que se cargue desde Firebase
            flagUrl: this.getCountryFlagUrl(mappedCountry, countryCode)
          });
          // Ordenar la lista nuevamente
          this.availableCountries.sort((a, b) => a.countryName.localeCompare(b.countryName));
        }
    
    // Limpiar números de provincia y ciudad si el país es España
    let finalLocality = locality;
    let finalProvince = administrativeAreaLevel2 || administrativeAreaLevel1;
    
    if (this.isSpain(mappedCountry)) {
      // Remover números de ciudad y provincia
      finalLocality = locality ? locality.replace(/[0-9]/g, '').trim() : '';
      finalProvince = finalProvince ? finalProvince.replace(/[0-9]/g, '').trim() : '';
    }
    
    // Actualizar el formulario
    const patchData = {
      street: fullAddress,
      locality: finalLocality,
      // Prefer level 2 (provincia) when available, otherwise keep level 1 to avoid empty data.
      province: finalProvince,
      postalCode: finalPostalCode,
      country: mappedCountry || 'España'
    };
    
    // console.log removed
    
    this.residenceForm.patchValue(patchData);
    
    // Actualizar el estado de España seleccionada
    this.isSpainSelected = (mappedCountry === 'España');
    

    // Forzar la actualización del formulario
    this.residenceForm.updateValueAndValidity();
    this.cdr.detectChanges();

    this.checkAddressDifference();
  }

  // Método para limpiar el formulario de dirección
  private clearAddressForm(): void {
    
    
    // Limpiar todos los campos de dirección
    this.residenceForm.patchValue({
      street: '',
      locality: '',
      province: '',
      postalCode: '',
      country: 'España' // Mantener España como valor por defecto
    });
    
    // Limpiar la dirección seleccionada
    this.selectedAddress = '';
    
    // Resetear el estado de España
    this.isSpainSelected = true;
    
    
  }

  // Método para normalizar nombres de países para comparación
  private normalizeCountryName(countryName: string): string {
    if (!countryName) return '';
    
    // Convertir a minúsculas y quitar espacios extra
    return countryName.toLowerCase().trim();
  }

  // Método para comparar países de forma normalizada
  private isSameCountry(country1: string, country2: string): boolean {
    if (!country1 || !country2) return false;
    
    const normalized1 = this.normalizeCountryName(country1);
    const normalized2 = this.normalizeCountryName(country2);
    
    // Comparación directa
    if (normalized1 === normalized2) return true;
    
    // Mapeo de variaciones comunes - expandido para incluir más variaciones
    const countryVariations: { [key: string]: string[] } = {
      'españa': ['spain', 'es', 'espana', 'esp'],
      'méxico': ['mexico', 'mx', 'mex'],
      'estados unidos': ['usa', 'us', 'united states', 'america', 'united states of america', 'estados unidos de américa', 'eeuu', 'ee.uu.'],
      'estados unidos de américa': ['usa', 'us', 'united states', 'america', 'united states of america', 'estados unidos', 'eeuu', 'ee.uu.'],
      'reino unido': ['uk', 'united kingdom', 'great britain', 'britain', 'gb'],
      'francia': ['france', 'fr'],
      'alemania': ['germany', 'de', 'deutschland'],
      'italia': ['italy', 'it'],
      'portugal': ['pt'],
      'canadá': ['canada', 'ca'],
      'brasil': ['brazil', 'br'],
      'argentina': ['ar'],
      'chile': ['cl'],
      'colombia': ['co'],
      'perú': ['peru', 'pe'],
      'venezuela': ['ve'],
      'ecuador': ['ec'],
      'bolivia': ['bo'],
      'uruguay': ['uy'],
      'paraguay': ['py']
    };
    
    // Buscar en las variaciones - verificar ambas direcciones
    for (const [key, variations] of Object.entries(countryVariations)) {
      const match1 = normalized1 === key || variations.includes(normalized1);
      const match2 = normalized2 === key || variations.includes(normalized2);
      
      if (match1 && match2) {
        return true;
      }
    }
    
    // Si no hay coincidencia en variaciones, comparar directamente normalizado
    // Esto cubre casos donde ambos países tienen el mismo nombre pero con diferentes acentos o mayúsculas
    return normalized1 === normalized2;
  }

  // Método para verificar si el país seleccionado es España
  private isSpain(country: string): boolean {
    if (!country) return false;
    const normalized = this.normalizeCountryName(country);
    return normalized === 'españa' || normalized === 'spain' || normalized === 'es' || normalized === 'espana' || normalized === 'esp';
  }

  // Método para determinar si se necesita certificado
  // Reglas:
  // 1. Si es el mismo país (cambio de dirección dentro del mismo país) → no necesita certificado
  // 2. Si es un país diferente (incl. cambio a España desde el extranjero) → necesita certificado
  private needsCertificate(selectedCountry: string, currentCountry: string): boolean {
    // Si es el mismo país, no necesita certificado (solo cambia de dirección dentro del mismo país)
    if (this.isSameCountry(selectedCountry, currentCountry)) {
      return false;
    }

    // Si es un país diferente, siempre necesita certificado (incluyendo cambio a España desde el extranjero)
    return true;
  }

  // Método para mapear países a nombres en español
  private mapCountryToSpanish(countryName: string): string {
    if (!countryName) return 'España';
    
    const countryMapping: { [key: string]: string } = {
      'Spain': 'España',
      'Portugal': 'Portugal', 
      'France': 'Francia',
      'Germany': 'Alemania',
      'Italy': 'Italia',
      'United Kingdom': 'Reino Unido',
      'Netherlands': 'Países Bajos',
      'Belgium': 'Bélgica',
      'Switzerland': 'Suiza',
      'Austria': 'Austria',
      'Poland': 'Polonia',
      'Czech Republic': 'República Checa',
      'Hungary': 'Hungría',
      'Sweden': 'Suecia',
      'Norway': 'Noruega',
      'Denmark': 'Dinamarca',
      'Finland': 'Finlandia',
      'United States': 'Estados Unidos',
      'United States of America': 'Estados Unidos',
      'USA': 'Estados Unidos',
      'US': 'Estados Unidos',
      'Canada': 'Canadá',
      'Mexico': 'México',
      'Brazil': 'Brasil',
      'Argentina': 'Argentina',
      'Chile': 'Chile',
      'Colombia': 'Colombia',
      'Peru': 'Perú',
      'Uruguay': 'Uruguay',
      'Ireland': 'Irlanda',
      'Luxembourg': 'Luxemburgo',
      'Greece': 'Grecia',
      'Cyprus': 'Chipre',
      'Malta': 'Malta',
      'Slovenia': 'Eslovenia',
      'Slovakia': 'Eslovaquia',
      'Croatia': 'Croacia',
      'Bulgaria': 'Bulgaria',
      'Romania': 'Rumania',
      'Lithuania': 'Lituania',
      'Latvia': 'Letonia',
      'Estonia': 'Estonia'
    };
    
    // Buscar coincidencia exacta primero
    let mappedCountry = countryMapping[countryName];
    
    // Si no hay coincidencia exacta, buscar por coincidencia parcial (case-insensitive)
    if (!mappedCountry) {
      const normalizedInput = countryName.toLowerCase().trim();
      for (const [key, value] of Object.entries(countryMapping)) {
        if (key.toLowerCase() === normalizedInput) {
          mappedCountry = value;
          break;
        }
      }
    }
    
    if (mappedCountry) {
      return mappedCountry;
    } else {
      // Si no se encuentra en el mapeo, retornar el nombre original
      return countryName;
    }
  }


  // Método para obtener el país seleccionado
  getActualCountry(): string {
    const selectedCountry = this.residenceForm.get('country')?.value;
    return selectedCountry || '';
  }

  /**
   * Obtiene el porcentaje de retención para un país
   * Primero busca en availableCountries (ya cargados), luego consulta Firebase si no lo encuentra
   */
  private async getCountryRetentionPercentage(countryName: string): Promise<number> {
    if (!countryName || countryName.trim() === '') {
      return 19; // Por defecto
    }

    const normalizedCountryName = countryName.trim();
    
    // Primero buscar en availableCountries que ya están cargados en memoria
    const country = this.availableCountries.find(c => 
      c.countryName.toLowerCase() === normalizedCountryName.toLowerCase()
    );
    
    if (country && country.percentage !== undefined) {
      console.log(`✅ Retención encontrada en availableCountries para ${normalizedCountryName}: ${country.percentage}%`);
      return country.percentage;
    }
    
    // Si no se encuentra en availableCountries, consultar Firebase usando el servicio
    console.log(`⚠️ País ${normalizedCountryName} no encontrado en availableCountries, consultando Firebase...`);
    return await this.retentionCalculationService.getCountryRetentionByNameAsync(normalizedCountryName);
  }

  // Método para manejar el cambio de país
  onCountryChange(): void {
    const selectedCountry = this.residenceForm.get('country')?.value;
    this.isSpainSelected = selectedCountry === 'España';
    
    // Obtener el país real para las comparaciones
    const actualCountry = this.getActualCountry();
    
    // Actualizar validadores del código postal según el país
    this.updatePostcodeValidators();
    
    // Verificar si se necesita certificado (mismo país = no, distinto país = sí, incl. cambio a España)
    this.showCertificateSection = this.needsCertificate(actualCountry, this.currentAddress.country);
    
    // Si el país no soporta autosuggest, sugerir modo manual pero no forzarlo
    // El usuario puede elegir entre autosuggest (si está disponible) o modo manual
    if (!this.isCountrySupportedForAutosuggest()) {
      // No forzar modo manual automáticamente
      // this.isManualAddress = true;
    }
    
    // Limpiar campos de dirección cuando cambia el país
    this.residenceForm.patchValue({
      street: '',
      locality: '',
      province: '',
      postalCode: ''
    });
    
    this.selectedAddress = '';
    this.showPostcodeReminder = false;
    
    // Verificar la diferencia de dirección después del cambio de país
    this.checkAddressDifference();
    
    // Forzar detección de cambios para evitar el error
    setTimeout(() => {
      this.cdr.detectChanges();
      // Verificar si el formulario está listo después del cambio de país
      this.checkFormReady();
    }, 0);
  }
  
  onCancel() {
    this.dialogRef.close({ success: false });
  }
}
